import React, { useEffect, useRef, useState } from "react";
import { Highlighter as HighlighterIcon, Eraser } from "lucide-react";

/**
 * v6.32.114 — WIDA-approved Highlighter (Version A: scratch-only).
 *
 * Behavior contract:
 *  - Wraps user-selected text inside the targeted container with <mark>
 *    tags whenever the toggle is ON.
 *  - Highlights are stored ONLY in the live DOM. They are NOT persisted
 *    to MongoDB and NOT included in the student's submitted answer.
 *  - Calling {clearOnNavigationKey} change (e.g. when the student moves
 *    to the next/previous question) wipes every <mark> in scope, so
 *    each question starts fresh, exactly like the WIDA paper highlighter.
 *  - "Clear highlights" button does the same wipe on demand.
 *
 * Props:
 *  - targetSelector  CSS selector (within the wrapping element) of the
 *                    DOM region the student is allowed to highlight.
 *                    Defaults to '[data-highlightable="true"]'.
 *  - clearOnNavigationKey   Anything that changes when the student
 *                    navigates to a new question (e.g. question id).
 *                    The component clears all highlights when this
 *                    value changes.
 *  - enabled         If false the toolbar renders nothing (used to
 *                    keep the student UI clean when no HL accommodation
 *                    is granted).
 */
const Highlighter = ({
  targetSelector = '[data-highlightable="true"]',
  clearOnNavigationKey,
  enabled = true,
}) => {
  const [isOn, setIsOn] = useState(false);
  const containerRef = useRef(null);

  // Clear highlights whenever the student navigates to a new question.
  useEffect(() => {
    clearAllHighlights();
    setIsOn(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearOnNavigationKey]);

  const getScopes = () => {
    const root = containerRef.current?.ownerDocument || document;
    return Array.from(root.querySelectorAll(targetSelector));
  };

  const clearAllHighlights = () => {
    const scopes = getScopes();
    scopes.forEach((scope) => {
      const marks = scope.querySelectorAll('mark[data-mosaic-highlight="1"]');
      marks.forEach((m) => {
        const parent = m.parentNode;
        if (!parent) return;
        while (m.firstChild) parent.insertBefore(m.firstChild, m);
        parent.removeChild(m);
        parent.normalize();
      });
    });
  };

  const isInsideAnyScope = (node, scopes) => {
    return scopes.some((s) => s.contains(node));
  };

  const wrapSelection = () => {
    if (!isOn) return;
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);

    const scopes = getScopes();
    if (
      !isInsideAnyScope(range.startContainer, scopes) ||
      !isInsideAnyScope(range.endContainer, scopes)
    ) {
      return;
    }
    // Only wrap when start + end live inside the SAME scope so we never
    // bridge across questions or the surrounding chrome.
    const containingScope = scopes.find(
      (s) => s.contains(range.startContainer) && s.contains(range.endContainer)
    );
    if (!containingScope) return;

    try {
      const mark = document.createElement("mark");
      mark.setAttribute("data-mosaic-highlight", "1");
      mark.style.backgroundColor = "#fde68a"; // amber-200
      mark.style.padding = "0 1px";
      mark.style.borderRadius = "2px";
      mark.appendChild(range.extractContents());
      range.insertNode(mark);
      sel.removeAllRanges();
    } catch {
      /* surrogate pairs / partial-node selections — silently no-op */
    }
  };

  if (!enabled) return null;

  return (
    <div
      ref={containerRef}
      onMouseUp={wrapSelection}
      data-testid="highlighter-toolbar-wrap"
    >
      <div className="flex items-center gap-2 mb-3 p-2 bg-amber-50 border-2 border-amber-200 rounded-md">
        <button
          type="button"
          onClick={() => setIsOn((v) => !v)}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wide border-2 transition-colors ${
            isOn
              ? "bg-amber-400 border-amber-500 text-amber-900"
              : "bg-white border-amber-300 text-amber-700 hover:bg-amber-100"
          }`}
          data-testid="highlighter-toggle"
          aria-pressed={isOn}
        >
          <HighlighterIcon size={14} />
          {isOn ? "Highlighter ON" : "Highlighter OFF"}
        </button>
        <button
          type="button"
          onClick={clearAllHighlights}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wide border-2 border-amber-300 text-amber-700 bg-white hover:bg-amber-100"
          data-testid="highlighter-clear"
        >
          <Eraser size={14} />
          Clear
        </button>
        <span className="text-[11px] text-amber-700 ml-1 italic" data-testid="highlighter-help">
          Select text in the passage or question to highlight it. Highlights are not saved.
        </span>
      </div>
    </div>
  );
};

export default Highlighter;
